/**
 * Hook for setting the payment method on a checkout session
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CheckoutData, CheckoutResource } from '../../core/resources/checkout';
import type { PaymentMethodName } from '../../core/resources/checkout';
import { getGlobalApiClient } from './useApiQuery';

export interface UseSetPaymentMethodOptions {
    sessionId?: string;
    checkout?: CheckoutData;
    onSuccess?: (paymentMethodName: PaymentMethodName) => void;
}

export interface UseSetPaymentMethodResult {
    setPaymentMethod: (paymentMethodName: PaymentMethodName) => Promise<void>;
    isPending: boolean;
    error: Error | null;
}

export function useSetPaymentMethod(
    options: UseSetPaymentMethodOptions = {},
): UseSetPaymentMethodResult {
    const { sessionId, checkout, onSuccess } = options;
    const queryClient = useQueryClient();

    const checkoutResource = useMemo(
        () => new CheckoutResource(getGlobalApiClient()),
        [],
    );

    const effectiveSessionId = sessionId ?? checkout?.checkoutSession?.id;

    const mutation = useMutation<
        { success: boolean },
        Error,
        PaymentMethodName
    >({
        mutationFn: (paymentMethodName) => {
            if (!effectiveSessionId) {
                throw new Error('No session ID available');
            }
            return checkoutResource.setPaymentMethod(effectiveSessionId, paymentMethodName);
        },
        onSuccess: async (_, paymentMethodName) => {
            await queryClient.invalidateQueries({ queryKey: ['checkout'] });
            onSuccess?.(paymentMethodName);
        },
    });

    const setPaymentMethod = async (paymentMethodName: PaymentMethodName) => {
        await mutation.mutateAsync(paymentMethodName);
    };

    return {
        setPaymentMethod,
        isPending: mutation.isPending,
        error: mutation.error,
    };
}
